import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Divider, Stack } from "@mui/material";
import { Navigate, useNavigate } from "react-router-dom";

import { useAuth } from "../../../../../app/providers/AuthProvider";
import StockPageHeader from "../../../components/StockPageHeader";
import {
  canAccessStockSubsection,
  getStockSubsectionByPath,
} from "../../../constants/stockAreas";
import {
  calculateProduction,
  createProduction,
  getProductionDirectIngredients,
  getProductionOptions,
} from "../api/productionsApi";
import ProductionCalculationForm, {
  PRODUCTION_MODES,
} from "../components/ProductionCalculationForm";
import ProductionCalculationResult from "../components/ProductionCalculationResult";
import { getProductionErrorMessage } from "../utils/productionError";
import { createProductionRequestId } from "../utils/productionRequestId";

const PRODUCTIONS_PATH = "/stock/operacion/produccion-elaborados";

function positiveQuantity(value) {
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (!/^\d+(?:\.\d{1,4})?$/.test(normalized)) return null;
  const number = Number(normalized);
  return Number.isFinite(number) && number > 0 ? number : null;
}

export default function CreateProductionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const calculationRequestId = useRef(0);
  const confirmationRequestId = useRef(null);
  const confirmingRef = useRef(false);
  const [productOptions, setProductOptions] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState("");
  const [productsReloadVersion, setProductsReloadVersion] = useState(0);
  const [product, setProduct] = useState(null);
  const [mode, setMode] = useState(PRODUCTION_MODES.FINAL);
  const [ingredientOptions, setIngredientOptions] = useState([]);
  const [ingredientsLoading, setIngredientsLoading] = useState(false);
  const [ingredientsError, setIngredientsError] = useState("");
  const [ingredientsReloadVersion, setIngredientsReloadVersion] = useState(0);
  const [ingredient, setIngredient] = useState(null);
  const [producedQuantity, setProducedQuantity] = useState("");
  const [basisQuantity, setBasisQuantity] = useState("");
  const [calculation, setCalculation] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [calculateError, setCalculateError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmationError, setConfirmationError] = useState("");
  const { subsection } = getStockSubsectionByPath(
    "operacion",
    "produccion-elaborados"
  );
  const canAccess = canAccessStockSubsection(subsection, user);

  function invalidateCalculation() {
    calculationRequestId.current += 1;
    confirmationRequestId.current = null;
    setCalculation(null);
    setCalculating(false);
    setCalculateError("");
    setConfirmationError("");
  }

  useEffect(() => {
    if (!canAccess) {
      setProductsLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setProductsLoading(true);
    setProductsError("");
    getProductionOptions({ signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) {
          setProductOptions(Array.isArray(response) ? response : []);
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setProductOptions([]);
          setProductsError(
            getProductionErrorMessage(
              error,
              "No se pudieron cargar los elaborados disponibles."
            )
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setProductsLoading(false);
      });
    return () => controller.abort();
  }, [canAccess, productsReloadVersion]);

  useEffect(() => {
    if (!canAccess || mode !== PRODUCTION_MODES.INGREDIENT || !product?.id) {
      setIngredientOptions([]);
      setIngredientsLoading(false);
      setIngredientsError("");
      return undefined;
    }

    const controller = new AbortController();
    const requestedProductId = Number(product.id);
    setIngredientsLoading(true);
    setIngredientsError("");
    getProductionDirectIngredients(requestedProductId, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!controller.signal.aborted) {
          setIngredientOptions(Array.isArray(response) ? response : []);
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setIngredientOptions([]);
          setIngredientsError(
            getProductionErrorMessage(
              error,
              "No se pudieron cargar los insumos directos."
            )
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setIngredientsLoading(false);
      });
    return () => controller.abort();
  }, [canAccess, ingredientsReloadVersion, mode, product?.id]);

  const canCalculate = useMemo(() => {
    if (!product?.id) return false;
    if (mode === PRODUCTION_MODES.FINAL) {
      return positiveQuantity(producedQuantity) !== null;
    }
    return Boolean(
      ingredient?.id && positiveQuantity(basisQuantity) !== null
    );
  }, [basisQuantity, ingredient?.id, mode, product?.id, producedQuantity]);

  function handleProductChange(nextProduct) {
    invalidateCalculation();
    setProduct(nextProduct);
    setIngredient(null);
    setIngredientOptions([]);
    setProducedQuantity("");
    setBasisQuantity("");
    if (!nextProduct?.supportsIngredientQuantity) {
      setMode(PRODUCTION_MODES.FINAL);
    }
  }

  function handleModeChange(nextMode) {
    if (nextMode === mode) return;
    invalidateCalculation();
    setMode(nextMode);
    setIngredient(null);
    setBasisQuantity("");
  }

  function handleIngredientChange(nextIngredient) {
    invalidateCalculation();
    setIngredient(nextIngredient);
    setBasisQuantity("");
  }

  function handleProducedQuantityChange(value) {
    invalidateCalculation();
    setProducedQuantity(value);
  }

  function handleBasisQuantityChange(value) {
    invalidateCalculation();
    setBasisQuantity(value);
  }

  async function handleCalculate() {
    if (!canCalculate || calculating) return;
    const requestId = ++calculationRequestId.current;
    const payload = buildProductionPayload();

    setCalculating(true);
    setCalculation(null);
    setCalculateError("");
    setConfirmationError("");
    confirmationRequestId.current = null;
    try {
      const response = await calculateProduction(payload);
      if (requestId === calculationRequestId.current) setCalculation(response);
    } catch (error) {
      if (requestId === calculationRequestId.current) {
        setCalculation(null);
        setCalculateError(
          getProductionErrorMessage(error, "No se pudo calcular la producción.")
        );
      }
    } finally {
      if (requestId === calculationRequestId.current) setCalculating(false);
    }
  }

  function buildProductionPayload() {
    return {
      preparedProductId: Number(product.id),
      calculationMode: mode,
      ...(mode === PRODUCTION_MODES.FINAL
        ? { producedQuantity: positiveQuantity(producedQuantity) }
        : {
            basisIngredientId: Number(ingredient.id),
            basisQuantity: positiveQuantity(basisQuantity),
          }),
    };
  }

  async function handleProduce() {
    if (!calculation?.canProduce || confirmingRef.current) return;
    confirmingRef.current = true;
    setConfirming(true);
    setConfirmationError("");

    if (!confirmationRequestId.current) {
      confirmationRequestId.current = createProductionRequestId();
    }

    try {
      await createProduction({
        ...buildProductionPayload(),
        requestId: confirmationRequestId.current,
      });
      confirmationRequestId.current = null;
      navigate(PRODUCTIONS_PATH, {
        state: { successMessage: "La producción fue creada correctamente." },
      });
    } catch (error) {
      const status = Number(error?.status || error?.response?.status || 0);
      const message = getProductionErrorMessage(
        error,
        "No se pudo confirmar la producción."
      );

      if (status === 409) {
        confirmationRequestId.current = null;
        calculationRequestId.current += 1;
        setCalculation(null);
        setCalculateError(`${message} Volvé a calcular antes de producir.`);
      } else {
        if (status >= 400 && status < 500) {
          confirmationRequestId.current = null;
        }
        setConfirmationError(
          status >= 500 || status === 0
            ? `${message} Podés reintentar la confirmación.`
            : message
        );
      }
    } finally {
      confirmingRef.current = false;
      setConfirming(false);
    }
  }

  if (!canAccess) {
    return <Navigate to="/stock/operacion" replace />;
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        width: "100%",
        minHeight: 0,
        minWidth: 0,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <StockPageHeader
        title="Producción de Elaborados"
        onBack={() => {
          if (!calculating && !confirming) navigate(PRODUCTIONS_PATH);
        }}
      />

      <Box
        sx={{
          p: { xs: 2, md: 3 },
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          bgcolor: "neutral.50",
          borderRadius: "24px",
          boxShadow: "inset 0px 0px 3px rgba(117, 117, 138, 0.22)",
          overflowX: "hidden",
          overflowY: "auto",
          scrollbarGutter: "stable",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(280px, 340px) auto minmax(0, 1fr)",
            },
            columnGap: { md: 3 },
            rowGap: 3,
            minHeight: "100%",
          }}
        >
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <ProductionCalculationForm
              product={product}
              productOptions={productOptions}
              productsLoading={productsLoading}
              productsError={productsError}
              mode={mode}
              ingredient={ingredient}
              ingredientOptions={ingredientOptions}
              ingredientsLoading={ingredientsLoading}
              ingredientsError={ingredientsError}
              producedQuantity={producedQuantity}
              basisQuantity={basisQuantity}
              calculating={calculating}
              disabled={confirming}
              calculateError={calculateError}
              canCalculate={canCalculate}
              onProductChange={handleProductChange}
              onProductsRetry={() =>
                setProductsReloadVersion((version) => version + 1)
              }
              onModeChange={handleModeChange}
              onIngredientChange={handleIngredientChange}
              onIngredientsRetry={() =>
                setIngredientsReloadVersion((version) => version + 1)
              }
              onProducedQuantityChange={handleProducedQuantityChange}
              onBasisQuantityChange={handleBasisQuantityChange}
              onCalculate={handleCalculate}
              onCancel={() => navigate(PRODUCTIONS_PATH)}
            />
          </Stack>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", md: "block" }, borderColor: "neutral.500" }}
          />

          <Stack
            spacing={1.5}
            sx={{
              minWidth: 0,
              minHeight: 0,
              overflow: "visible",
            }}
          >
            <ProductionCalculationResult
              calculation={calculation}
              confirming={confirming}
              confirmationError={confirmationError}
              onProduce={handleProduce}
            />
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
}
